import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';
import { Scale, FileText, Loader2, CheckCircle2, Archive, ExternalLink, Clock, Link2, AlertTriangle } from 'lucide-react';
import AccessGate from '../components/AccessGate';
import { useAuth } from '../context/AuthContext';

const STATUS = {
  carried: { label: 'Carried', color: '#1c6b2c' },
  rejected: { label: 'Not carried', color: '#8a1f11' },
  tied: { label: 'Tied', color: '#9a6b00' },
  open: { label: 'Open', color: '#1F3A5F' },
};
const AUTH_COLOR = {
  committee: '#1F3A5F', general_meeting: '#1c6b2c', special_resolution: '#8a1f11',
  board: '#6b3fa0', legislation: '#9a6b00', unknown: '#7a7a6a',
};
const POLICY_STATUS = { current: 'Current', under_review: 'Under review', superseded: 'Superseded' };
const fmt = (d) => { if (!d) return ''; try { return new Date(d).toLocaleDateString([], { dateStyle: 'medium' }); } catch { return d; } };

export default function Governance() {
  const { governanceMotions, motionLifecycle, govListDocs, isAdmin, canAccess } = useAuth();
  const [rows, setRows] = useState([]);
  const [docs, setDocs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showArchived, setShowArchived] = useState(false);
  const [showRejected, setShowRejected] = useState(false);
  const canManage = isAdmin || canAccess('admin-meetings');

  const load = () => {
    setLoading(true);
    Promise.all([
      governanceMotions().catch(() => []),
      govListDocs().catch(() => []),
    ]).then(([m, d]) => { setRows(m); setDocs(d); }).finally(() => setLoading(false));
  };
  useEffect(load, [governanceMotions, govListDocs]);

  const visible = rows.filter((r) => (showArchived || !r.archived) && (showRejected || r.status !== 'rejected'));

  const setLifecycle = async (r, body) => {
    try { await motionLifecycle(r.meeting_id, r.motion_id, body); toast.success('Updated.'); load(); }
    catch (e) { toast.error('Could not update the motion.'); }
  };

  return (
    <AccessGate pageKey="governance">
      <div className="max-w-4xl mx-auto px-4 py-10 fade-in">
        <div className="flex items-center gap-2">
          <Scale size={26} style={{ color: 'var(--maroon)' }} />
          <h1 className="font-title text-3xl md:text-4xl" style={{ color: 'var(--maroon)' }}>Governance</h1>
        </div>
        <div className="h-1 w-20 mt-2" style={{ background: 'var(--gold)' }} />
        <p className="text-sm mt-3 max-w-2xl" style={{ color: 'var(--olive)' }}>The register of committee motions (policies). Each links back to the meeting minutes where it was decided.</p>

        {docs.length > 0 && (
          <div className="mt-8" data-testid="gov-policies">
            <h2 className="font-title text-xl" style={{ color: 'var(--navy)' }}>Policies from governing documents</h2>
            <p className="text-xs mt-1 mb-4 max-w-2xl" style={{ color: 'var(--olive)' }}>Each policy shows the authority required to change it, and any affirmed committee motions that relate to it.</p>
            {docs.reduce((acc, d) => acc + (d.policies || []).filter((p) => p.has_conflict).length, 0) > 0 && (
              <div className="flex items-start gap-2 rounded-lg p-3 mb-4 text-sm" style={{ background: '#f9e4e0', border: '1px solid #8a1f11', color: '#8a1f11' }} data-testid="gov-conflict-banner">
                <AlertTriangle size={16} className="mt-0.5 flex-shrink-0" />
                <span>Some policies are linked to a motion decided at a <strong>lower authority</strong> than the policy requires to change it. These are flagged below as a possible invalid decision.</span>
              </div>
            )}
            {docs.map((doc) => (
              <div key={doc.id} className="mb-6" data-testid={`gov-doc-${doc.id}`}>
                <div className="flex items-center gap-2 mb-2 text-sm font-title" style={{ color: 'var(--maroon)' }}><FileText size={15} /> {doc.name}</div>
                <div className="space-y-2">
                  {(doc.policies || []).map((p) => {
                    const col = AUTH_COLOR[p.authority_required] || AUTH_COLOR.unknown;
                    return (
                      <div key={p.id} className="bg-white rounded-lg border p-4" style={{ borderColor: p.has_conflict ? '#8a1f11' : 'rgba(0,0,0,0.08)', opacity: p.status === 'superseded' ? 0.65 : 1 }} data-testid={`gov-policy-${p.id}`}>
                        <div className="flex items-start justify-between gap-3 flex-wrap">
                          <div className="font-title text-base flex items-center gap-2" style={{ color: 'var(--navy)' }}>{p.title}{p.has_conflict && <AlertTriangle size={15} style={{ color: '#8a1f11' }} data-testid={`gov-policy-conflict-${p.id}`} />}</div>
                          <span className="px-2 py-0.5 rounded-full text-xs font-title flex-shrink-0" style={{ color: col, border: `1px solid ${col}` }} data-testid={`gov-policy-authority-${p.id}`}>{p.authority_label}</span>
                        </div>
                        {p.summary && <p className="text-sm mt-1.5" style={{ color: 'var(--olive)' }}>{p.summary}</p>}
                        {p.content && (
                          <details className="mt-2" data-testid={`gov-policy-content-${p.id}`}>
                            <summary className="text-xs cursor-pointer select-none" style={{ color: 'var(--maroon)' }}>Read the clause{p.reference ? ` (${p.reference})` : ''}</summary>
                            <blockquote className="mt-2 pl-3 text-sm leading-relaxed border-l-2 whitespace-pre-line" style={{ borderColor: 'var(--gold)', color: '#2b2b2b', fontFamily: 'Georgia, serif' }}>{p.content}</blockquote>
                          </details>
                        )}
                        <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-xs mt-2" style={{ color: 'var(--olive)' }}>
                          {p.reference && <span>{p.reference}</span>}
                          {p.status && p.status !== 'current' && <span className="italic">{POLICY_STATUS[p.status]}</span>}
                        </div>
                        {(p.matched || []).length > 0 && (
                          <div className="mt-3 pt-2 border-t flex flex-wrap gap-2 items-center" style={{ borderColor: 'rgba(0,0,0,0.06)' }}>
                            <span className="text-xs inline-flex items-center gap-1" style={{ color: 'var(--maroon)' }}><Link2 size={13} /> Related motions:</span>
                            {(p.matched || []).map((mm) => (
                              <Link key={mm.motion_id} to={`/meetings/${mm.meeting_id}`} className="text-xs px-2 py-0.5 rounded hover:underline inline-flex items-center gap-1" style={{ background: mm.conflict ? '#f9e4e0' : 'var(--sage)', color: mm.conflict ? '#8a1f11' : 'var(--navy)', border: mm.conflict ? '1px solid #8a1f11' : 'none' }} data-testid={`gov-policy-motion-${p.id}-${mm.motion_id}`}>{mm.conflict && <AlertTriangle size={11} />}{mm.question}</Link>
                            ))}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>
        )}

        <h2 className="font-title text-xl mt-8" style={{ color: 'var(--navy)' }}>Motions register</h2>

        <div className="flex flex-wrap gap-4 mt-5 text-sm" style={{ color: 'var(--navy)' }}>
          <label className="inline-flex items-center gap-1.5"><input type="checkbox" checked={showRejected} onChange={(e) => setShowRejected(e.target.checked)} data-testid="gov-show-rejected" /> Show rejected</label>
          <label className="inline-flex items-center gap-1.5"><input type="checkbox" checked={showArchived} onChange={(e) => setShowArchived(e.target.checked)} data-testid="gov-show-archived" /> Show completed/archived</label>
        </div>

        {loading ? <p className="mt-6 flex items-center gap-2 text-sm" style={{ color: 'var(--olive)' }}><Loader2 size={15} className="animate-spin" /> Loading…</p>
          : visible.length === 0 ? <p className="mt-6 text-sm" style={{ color: 'var(--olive)' }} data-testid="gov-empty">No motions to show.</p>
          : (
            <div className="mt-6 space-y-3" data-testid="gov-motions">
              {visible.map((r) => {
                const st = STATUS[r.status] || STATUS.open;
                return (
                  <div key={r.motion_id} className="bg-white rounded-lg border p-4" style={{ borderColor: 'rgba(0,0,0,0.08)', opacity: r.archived ? 0.7 : 1 }} data-testid={`gov-motion-${r.motion_id}`}>
                    <div className="flex items-start justify-between gap-3 flex-wrap">
                      <div className="min-w-0">
                        <div className="font-title text-lg" style={{ color: 'var(--navy)' }}>{r.question}</div>
                        <div className="flex flex-wrap items-center gap-2 text-xs mt-1" style={{ color: 'var(--olive)' }}>
                          <span className="px-2 py-0.5 rounded-full font-title" style={{ background: 'white', color: st.color, border: `1px solid ${st.color}` }}>{st.label}</span>
                          <span>Y {r.tally.yes} · N {r.tally.no} · A {r.tally.abstain}</span>
                          {r.completed && <span className="inline-flex items-center gap-1"><CheckCircle2 size={12} /> completed</span>}
                          {r.archived && <span className="inline-flex items-center gap-1"><Archive size={12} /> archived</span>}
                          {r.expiry_date && <span className="inline-flex items-center gap-1"><Clock size={12} /> expires {fmt(r.expiry_date)}</span>}
                        </div>
                      </div>
                      <Link to={`/meetings/${r.meeting_id}`} className="inline-flex items-center gap-1 text-sm hover:underline flex-shrink-0" style={{ color: 'var(--maroon)' }} data-testid={`gov-link-${r.motion_id}`}>
                        <FileText size={14} /> {r.meeting_title || 'Minutes'} <ExternalLink size={12} />
                      </Link>
                    </div>
                    {canManage && (
                      <div className="flex flex-wrap items-center gap-2 mt-3 pt-3 border-t" style={{ borderColor: 'rgba(0,0,0,0.06)' }}>
                        <button onClick={() => setLifecycle(r, { completed: !r.completed })} className="text-xs px-2.5 py-1 rounded" style={{ background: 'var(--sage)', color: 'var(--navy)' }} data-testid={`gov-complete-${r.motion_id}`}>{r.completed ? 'Mark active' : 'Mark completed'}</button>
                        <button onClick={() => setLifecycle(r, { archived: !r.archived })} className="text-xs px-2.5 py-1 rounded" style={{ background: 'var(--sage)', color: 'var(--navy)' }} data-testid={`gov-archive-${r.motion_id}`}>{r.archived ? 'Unarchive' : 'Archive'}</button>
                        <label className="text-xs inline-flex items-center gap-1" style={{ color: 'var(--olive)' }}>Expiry
                          <input type="date" defaultValue={r.expiry_date ? String(r.expiry_date).slice(0, 10) : ''} onChange={(e) => setLifecycle(r, { expiry_date: e.target.value })} data-testid={`gov-expiry-${r.motion_id}`} className="border rounded px-1 py-0.5" style={{ borderColor: 'rgba(0,0,0,0.2)' }} />
                        </label>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
      </div>
    </AccessGate>
  );
}
